/**
 * The single congestion ladder.
 *
 * Both the host's own stats poll and a guest `clientStats` message are normalized into
 * `StreamCongestionInputs` and evaluated here. There is deliberately no second implementation:
 * when the guest and host each owned a ladder, the guest's absolute-RTT one classified every
 * link with ≥100 ms of propagation as congested, and the resulting message stream held the
 * host's recovery episode open forever.
 *
 * LOAD-BEARING INVARIANT — the verdict is a function of DIFFERENCES only: queueing above a
 * per-peer RTT floor, a loss rate, latency growth above a recent minimum, a decode backlog.
 * It never reads an absolute RTT. That is what makes a guest 180 ms away on a clean link
 * indistinguishable from a nearby guest on the same clean link, and it is pinned by the
 * propagation-invariance test in streamCongestionSignal.test.ts.
 *
 * Adding an absolute-RTT input to this module reintroduces the original defect. Don't.
 *
 * The same invariance applies to rates: every decode-rate judgement is made against what the
 * host actually DELIVERED, never what it intended to send. A threshold read against intent
 * is a feedback loop — the host's own cut depresses the guest's decode rate, which reads as
 * the guest degrading, which justifies the next cut.
 */

import { decoderDutyCycle, DECODER_SATURATION_RATIO } from './decodeLagContracts';
import type { CongestionSignalAvailability } from './framePipeline/framePipelineContracts';
import {
  RECOVERY_STANDING_QUEUE_MS_ENTER,
  SUSTAINED_STANDING_QUEUE_MS_ENTER,
  TARGET_FRAMERATE,
} from './webrtcLatencyContracts';

export type StreamCongestionInputs = {
  /**
   * Excess queueing above this peer's RTT floor, or null while the floor is untrusted.
   * Null is not evidence of health — see the `insufficient-data` verdict.
   */
  standingQueueMs: number | null;
  /**
   * Loss over the sample interval as a RATE, percent. A rate rather than a packet count:
   * the same 50 lost packets is ~0.7% of an 8 Mbps stream and ~4% of a 1.5 Mbps one, so a
   * count-based trigger gets harder to satisfy exactly as the stream is cut.
   */
  packetsLostPct?: number;
  /** Growth of playout delay above its recent window minimum, ms. */
  playoutLatencyTrendMs?: number;
  /** Decode backlog (framesReceived − framesDecoded) over the sample interval. */
  decodeBacklogFrames?: number;
  /** freezeCount increase over the sample interval. */
  freezeDelta?: number;
  /**
   * Inbound decode framerate as sampled by the guest this interval (inbound-rtp
   * framesPerSecond). The one measurement whose drop no other input stands in for: a stream
   * starving the decoder produces no queue (packets drain as fast as they arrive), no loss
   * (nothing is missing, just late), no freeze counter (Safari emits none) and no playout
   * growth (the decoder is never ahead). On that shape - the 2fps Safari case - decode FPS
   * alone moves, so only decode FPS can trigger.
   */
  decodeFps?: number;
  /**
   * The framerate the host's encoder MEASURABLY put on the wire for this peer, when the
   * caller knows it. The decode-FPS thresholds are read relative to it: a decoder that keeps
   * pace with the 30fps it was actually sent is healthy, not "half rate". Absent means the
   * thresholds fall back to the absolute 60fps defaults.
   *
   * This must be the delivered rate, never the configured one. A guest cannot decode frames
   * the host never sent, so the configured rate closes a loop: a host-side cut lowers the
   * guest's decode rate, which reads as congestion, which justifies the next cut. Field data:
   * a host that had ratcheted itself to 17fps encode read its guest's honest 18fps decode as
   * `elevated` on every tick for ten minutes and never let the stream climb back.
   */
  deliveredFps?: number;
  /**
   * Wall time an average frame spent inside the guest's decoder this interval, ms, when the
   * engine reports it. Distinguishes a decoder that cannot keep up from one that was not
   * given much to do — see `decodeFpsSignalApplies`.
   */
  decodeMsPerFrame?: number;
  /**
   * Which signals this guest's engine can measure at all (`signalAvailabilityFor`). A signal
   * marked unavailable is IGNORED even when the field is present: a verdict citing a number
   * the engine cannot measure is a verdict built on a fabricated number (Safari's synthetic
   * playout trend). Absent means every present field is trusted — the pre-existing behaviour.
   */
  availability?: CongestionSignalAvailability;
};

/**
 * The sample with every field its engine cannot measure removed, per `availability`. The
 * identity when no availability is attached. Every evaluation below reads through this so
 * the verdict, the decisive-signal name and the backlog dwell all agree on what was judged.
 */
export function withAvailableSignals(inputs: StreamCongestionInputs): StreamCongestionInputs {
  const availability = inputs.availability;
  if (!availability) return inputs;
  const gated: StreamCongestionInputs = { ...inputs };
  if (!availability.playoutTrend) delete gated.playoutLatencyTrendMs;
  if (!availability.decodeBacklog) delete gated.decodeBacklogFrames;
  if (!availability.freeze) delete gated.freezeDelta;
  if (!availability.decodeResidency) delete gated.decodeMsPerFrame;
  return gated;
}

export type CongestionVerdict = 'none' | 'elevated' | 'critical' | 'insufficient-data';

export type CongestionThresholds = {
  /** Instantaneous standing queue that is critical. */
  queueEnterMs: number;
  /** Standing queue that is elevated. */
  sustainedQueueEnterMs: number;
  /** Loss rate that is critical regardless of distance, percent. */
  lossCriticalPct: number;
  /** Loss rate that is elevated, percent. */
  lossElevatedPct: number;
  /** Playout-latency growth that is critical, ms. */
  playoutTrendCriticalMs: number;
  /** Playout-latency growth that is elevated, ms. */
  playoutTrendElevatedMs: number;
  /** Decode backlog that is critical, frames. */
  decodeBacklogCriticalFrames: number;
  /** Decode backlog held past decodeBacklogSustainedMs that is critical, frames. */
  decodeBacklogSustainedFrames: number;
  /** How long a decode backlog must hold before it counts as sustained, ms. */
  decodeBacklogSustainedMs: number;
  /** freezeCount delta that is critical. */
  freezeCriticalDelta: number;
  /**
   * Decode framerate at or below which the stream is starving, at the 60fps reference rate.
   * Scaled by `deliveredFps / TARGET_FRAMERATE` when the sample carries a delivered rate —
   * see `decodeFpsLimitsFor`.
   */
  decodeFpsCritical: number;
  /** Decode framerate clearly below the delivered rate, at the 60fps reference rate. */
  decodeFpsElevated: number;
};

/**
 * UNVALIDATED ESTIMATES, except the two queue thresholds which are the existing host
 * constants. The load-bearing part is the ordering: every elevated threshold sits strictly
 * below its critical counterpart, and the elevated queue sits below the critical queue so a
 * spike still trips the fast path first.
 *
 * The playout-latency thresholds are the only signal that sees decode-side latency growth.
 * On mobile, jitterBufferTarget is pinned to 0 and RTT stays flat while a decoder falls
 * behind, so neither RTT nor freezeCount moves — growth above the window minimum does.
 */
export const DEFAULT_CONGESTION_THRESHOLDS: CongestionThresholds = {
  queueEnterMs: RECOVERY_STANDING_QUEUE_MS_ENTER,
  sustainedQueueEnterMs: SUSTAINED_STANDING_QUEUE_MS_ENTER,
  lossCriticalPct: 5,
  lossElevatedPct: 2,
  playoutTrendCriticalMs: 250,
  playoutTrendElevatedMs: 100,
  decodeBacklogCriticalFrames: 30,
  decodeBacklogSustainedFrames: 12,
  decodeBacklogSustainedMs: 3_000,
  freezeCriticalDelta: 3,
  // Framerate thresholds chosen against TARGET_FRAMERATE (60) and SCALED to the encoder's
  // delivered rate when a sample carries one (`decodeFpsLimitsFor`). Decode FPS is a rate
  // measured against the rate delivered, so the meaningful quantity is the ratio: a drop to
  // five-sixths of it is worth an early warn; a collapse to half rate or below is past what
  // bitrate alone can be assumed to fix. Left absolute, these read a guest the host stepped
  // down to 30fps as permanently starving the moment it decodes exactly 30.
  decodeFpsCritical: 30,
  decodeFpsElevated: 50,
};

/** The reference framerate the absolute decode-FPS thresholds were chosen against. */
export const DECODE_FPS_REFERENCE_TARGET = TARGET_FRAMERATE;

/**
 * The decode-FPS thresholds a sample is judged against, resolved for the rate delivered.
 *
 * The absolute defaults (30 critical / 50 elevated) encode two ratios against a 60fps
 * stream: "half rate or worse" and "a sixth below rate". Those ratios are what mean
 * something on every stream; the absolute numbers only mean it at 60fps. A guest the host
 * is sending 30fps decodes at 30 when it is keeping up perfectly — judging it against the
 * 60fps numbers reads a healthy decoder as starving, forever. Without a delivered rate the
 * absolute defaults stand, so a caller that cannot measure one gets the old behavior.
 */
export function decodeFpsLimitsFor(
  deliveredFps: number | undefined,
  limits: Pick<CongestionThresholds, 'decodeFpsCritical' | 'decodeFpsElevated'> = DEFAULT_CONGESTION_THRESHOLDS,
): { critical: number; elevated: number } {
  if (typeof deliveredFps !== 'number' || !Number.isFinite(deliveredFps) || deliveredFps <= 0) {
    return { critical: limits.decodeFpsCritical, elevated: limits.decodeFpsElevated };
  }
  const scale = deliveredFps / DECODE_FPS_REFERENCE_TARGET;
  return {
    critical: limits.decodeFpsCritical * scale,
    elevated: limits.decodeFpsElevated * scale,
  };
}

/**
 * Whether a low decode rate is evidence of a starving decoder at all.
 *
 * The decode-FPS signal exists for ONE shape: a decoder that cannot keep up while every
 * other meter reads clean (the 2fps Safari case). A decoder with idle time to spare is not
 * that shape — whatever is limiting its rate is upstream of it, and cutting bitrate or
 * framerate cannot help. Field data: an Android decoder at a 22% duty cycle, idle better
 * than three quarters of every second, drove a permanent `elevated` verdict.
 *
 * Only suppresses where residency was actually measured; engines that report none keep the
 * rate-only behaviour.
 */
export function decodeFpsSignalApplies(inputs: Pick<StreamCongestionInputs, 'decodeFps' | 'decodeMsPerFrame'>): boolean {
  const dutyCycle = decoderDutyCycle(inputs.decodeFps ?? Number.NaN, inputs.decodeMsPerFrame);
  return dutyCycle === null || dutyCycle >= DECODER_SATURATION_RATIO;
}

/** The two critical thresholds the backlog dedup judges its inputs against. */
type BacklogCriticalThresholds = Pick<
  CongestionThresholds,
  'playoutTrendCriticalMs' | 'decodeBacklogCriticalFrames'
>;

/**
 * Collapse the two backlog-derived ladder inputs into ONE signal.
 *
 * On Android (jitterBufferPinned) playoutLatencyTrendMs IS the decode backlog converted to
 * playback time; decodeBacklogFrames is the same backlog in frames. Feeding both into the
 * ladder would double-count one backlog. The dedup rule: when both are present and consistent
 * (both non-null and finite), keep the one that exceeds its own critical threshold when either
 * does; otherwise keep the larger of the two. The winner is playoutLatencyTrendMs when the
 * guest's decoder is behind (it is the only signal that moves on a pinned jitter buffer),
 * otherwise decodeBacklogFrames.
 *
 * `thresholds` are the critical thresholds the ladder is evaluating with — the caller's
 * overrides, when it has any — so the dedup and the verdict never disagree about which
 * reading is the critical one.
 */
export function resolveCongestionSignals(
  inputs: {
    playoutLatencyTrendMs?: number;
    decodeBacklogFrames?: number;
  },
  thresholds: BacklogCriticalThresholds = DEFAULT_CONGESTION_THRESHOLDS,
): {
  playoutLatencyTrendMs?: number;
  decodeBacklogFrames?: number;
} {
  const trend = inputs.playoutLatencyTrendMs;
  const backlog = inputs.decodeBacklogFrames;
  const hasTrend = typeof trend === 'number' && Number.isFinite(trend);
  const hasBacklog = typeof backlog === 'number' && Number.isFinite(backlog);
  if (hasTrend && hasBacklog) {
    // Both derive from the same decode backlog - keep the larger as the single signal.
    // On Android the playout-trend is the backlog in ms (the larger, more sensitive number);
    // the frames count is the coarser companion. Keep the larger, null the other.
    // The two scales differ (ms vs frames), so "larger" is not meaningful across them; the
    // rule is: keep whichever is above its own critical threshold when either is, else the
    // larger of the two raw values.
    const trendCritical = trend! >= thresholds.playoutTrendCriticalMs;
    const backlogCritical = backlog! >= thresholds.decodeBacklogCriticalFrames;
    if (trendCritical && !backlogCritical) return { playoutLatencyTrendMs: trend };
    if (backlogCritical && !trendCritical) return { decodeBacklogFrames: backlog };
    // Both critical or both below: keep the larger raw value.
    if (trend! >= backlog!) {
      return { playoutLatencyTrendMs: trend };
    }
    return { decodeBacklogFrames: backlog };
  }
  // Single-signal path: drop a non-finite value entirely (never let NaN through).
  return {
    ...(hasTrend ? { playoutLatencyTrendMs: trend } : {}),
    ...(hasBacklog ? { decodeBacklogFrames: backlog } : {}),
  };
}

/** True when the value is a usable measurement rather than an absent or non-finite one. */
function measured(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * The same sample with both backlog-derived inputs removed — what the ladder sees of it
 * once the deduped backlog is set aside. Evaluating this tells a caller whether a verdict
 * rests on the backlog alone (which must dwell before it may hard-cut) or on a non-backlog
 * signal. Of those, loss / freeze / decode FPS hard-cut at once; standing queue must dwell
 * (Safari single-sample RTT blips otherwise cliff the stream).
 */
export function withoutBacklogSignals(inputs: StreamCongestionInputs): StreamCongestionInputs {
  const stripped: StreamCongestionInputs = { ...inputs };
  delete stripped.playoutLatencyTrendMs;
  delete stripped.decodeBacklogFrames;
  return stripped;
}

/**
 * Zero the standing-queue reading so the ladder can judge whether a critical rests on
 * loss / freeze / decode FPS alone. Callers dwell a standing-queue-only critical before
 * hard-cutting; a critical that survives this strip is network-proven and stays instant.
 */
export function withoutStandingQueue(inputs: StreamCongestionInputs): StreamCongestionInputs {
  return { ...inputs, standingQueueMs: 0 };
}

/** The signal that decided a verdict, for debug lines — mirrors the ladder's own ordering. */
export type CongestionDecisiveSignal =
  | 'standing-queue'
  | 'loss'
  | 'decode-fps'
  | 'decode-backlog'
  | 'playout-trend'
  | 'freeze'
  | 'none';

/**
 * Name the input that produced a verdict, judged against the SAME resolved thresholds the
 * ladder used (including the target-relative decode-FPS limits). Debug-only: a replay reads
 * the trigger at a glance, and it must not disagree with the verdict beside it.
 */
export function decisiveCongestionSignal(
  rawInputs: StreamCongestionInputs,
  verdict: CongestionVerdict,
  thresholds: Partial<CongestionThresholds> = {},
): CongestionDecisiveSignal {
  const inputs = withAvailableSignals(rawInputs);
  const limits: CongestionThresholds = { ...DEFAULT_CONGESTION_THRESHOLDS, ...thresholds };
  const fps = decodeFpsLimitsFor(inputs.deliveredFps, limits);
  const fpsApplies = decodeFpsSignalApplies(inputs);
  const queue = inputs.standingQueueMs;
  if (verdict === 'critical') {
    if (queue !== null && measured(queue) && queue >= limits.queueEnterMs) return 'standing-queue';
    if (measured(inputs.packetsLostPct) && inputs.packetsLostPct >= limits.lossCriticalPct) return 'loss';
    if (fpsApplies && measured(inputs.decodeFps) && inputs.decodeFps <= fps.critical) return 'decode-fps';
    if (measured(inputs.decodeBacklogFrames) && inputs.decodeBacklogFrames >= limits.decodeBacklogCriticalFrames) {
      return 'decode-backlog';
    }
    if (measured(inputs.playoutLatencyTrendMs) && inputs.playoutLatencyTrendMs >= limits.playoutTrendCriticalMs) {
      return 'playout-trend';
    }
    return 'freeze';
  }
  if (verdict === 'elevated') {
    if (fpsApplies && measured(inputs.decodeFps) && inputs.decodeFps <= fps.elevated) return 'decode-fps';
    if (measured(inputs.packetsLostPct) && inputs.packetsLostPct >= limits.lossElevatedPct) return 'loss';
    if (measured(inputs.playoutLatencyTrendMs) && inputs.playoutLatencyTrendMs >= limits.playoutTrendElevatedMs) {
      return 'playout-trend';
    }
    return 'standing-queue';
  }
  return 'none';
}

/** `backlogCriticalRatio` at or above this is a critical backlog. */
export const BACKLOG_CRITICAL_RATIO = 1;

/**
 * The deduped backlog signal as a fraction of its own critical threshold, or undefined when
 * neither backlog input is measured. `>= BACKLOG_CRITICAL_RATIO` means the reading the dedup
 * kept — playout-trend in ms or decode backlog in frames — is critical.
 *
 * Unitless on purpose: the guest may report the same backlog in either unit from one tick
 * to the next (the dedup keeps whichever is critical), and a dwell clock that answers "how
 * long has the backlog been critical" must not restart when the unit flips.
 */
export function backlogCriticalRatio(
  inputs: StreamCongestionInputs,
  thresholds: Partial<CongestionThresholds> = {},
): number | undefined {
  const limits: CongestionThresholds = { ...DEFAULT_CONGESTION_THRESHOLDS, ...thresholds };
  const deduped = resolveCongestionSignals(withAvailableSignals(inputs), limits);
  if (measured(deduped.playoutLatencyTrendMs)) {
    return deduped.playoutLatencyTrendMs / limits.playoutTrendCriticalMs;
  }
  if (measured(deduped.decodeBacklogFrames)) {
    return deduped.decodeBacklogFrames / limits.decodeBacklogCriticalFrames;
  }
  return undefined;
}

/**
 * Tracks how long a decode backlog has held above its trigger, so a *constant* backlog —
 * the Android shape, where the decoder falls behind and stays behind — escalates the same
 * way a growing one does. Playout-latency growth cannot see a steady backlog: it measures
 * growth above a window minimum, and a backlog that never shrinks has no growth.
 */
export class DecodeBacklogDwell {
  private sinceMs: number | null = null;

  constructor(
    private readonly options: {
      /** Backlog (frames) at or above which the dwell starts. */
      thresholdFrames: number;
      /** How long the backlog must hold before it trips, ms. */
      holdMs: number;
      nowMs?: () => number;
    },
  ) {}

  private nowMs(): number {
    return this.options.nowMs?.() ?? Date.now();
  }

  /**
   * Observe one sample.
   *
   * @returns 'trip' when the backlog has held at or above the threshold for the full window.
   *          'holding' while it is accumulating. 'clear' while it is below the threshold.
   */
  public observe(backlogFrames: number | undefined): 'trip' | 'holding' | 'clear' {
    if (backlogFrames === undefined || !Number.isFinite(backlogFrames)) {
      this.sinceMs = null;
      return 'clear';
    }
    if (backlogFrames < this.options.thresholdFrames) {
      this.sinceMs = null;
      return 'clear';
    }
    const now = this.nowMs();
    if (this.sinceMs === null) this.sinceMs = now;
    if (now - this.sinceMs >= this.options.holdMs) return 'trip';
    return 'holding';
  }

  public reset(): void {
    this.sinceMs = null;
  }
}

/**
 * Evaluate one sample against the ladder.
 *
 * Returns `insufficient-data` when the RTT floor is untrusted and no distance-independent
 * signal (loss, freezes, decode backlog, latency growth) fires. Callers must treat that as
 * "hold the current state" — it may neither enter recovery nor advance an exit — because an
 * untrusted floor is not evidence of health any more than it is evidence of congestion.
 */
export function evaluateCongestionSignal(
  rawInputs: StreamCongestionInputs,
  thresholds: Partial<CongestionThresholds> = {},
): CongestionVerdict {
  const limits: CongestionThresholds = { ...DEFAULT_CONGESTION_THRESHOLDS, ...thresholds };
  // AVAILABILITY FIRST: a field the engine cannot measure is dropped before anything reads
  // it, so Safari's synthetic playout trend never decides a verdict (Phase 1 contract).
  const inputs = withAvailableSignals(rawInputs);

  // DEDUP FIRST: the two backlog-derived inputs (playoutLatencyTrendMs + decodeBacklogFrames)
  // come from the same decode backlog in the guest sampler (Android's pinned-jitter path),
  // so one backlog could otherwise fire two criticals in the same tick. Collapse them to a
  // single signal before any evaluation (Phase 1 contract) — both tiers below read only the
  // deduped pair, never the raw inputs.
  const deduped = resolveCongestionSignals(
    {
      playoutLatencyTrendMs: inputs.playoutLatencyTrendMs,
      decodeBacklogFrames: inputs.decodeBacklogFrames,
    },
    limits,
  );
  const playoutLatencyTrendMs = deduped.playoutLatencyTrendMs;
  const decodeBacklogFrames = deduped.decodeBacklogFrames;
  // Decode-FPS thresholds follow the rate this peer was actually sent, not the 60fps
  // reference, and only apply at all while the decoder is not demonstrably idle.
  const fpsLimits = decodeFpsLimitsFor(inputs.deliveredFps, limits);
  const decodeFpsApplies = decodeFpsSignalApplies(inputs);

  // Distance-independent signals first: these stay valid even while the floor is untrusted,
  // because none of them scales with propagation.
  if (measured(inputs.packetsLostPct) && inputs.packetsLostPct >= limits.lossCriticalPct) {
    return 'critical';
  }
  if (measured(inputs.freezeDelta) && inputs.freezeDelta >= limits.freezeCriticalDelta) {
    return 'critical';
  }
  if (
    measured(decodeBacklogFrames) &&
    decodeBacklogFrames >= limits.decodeBacklogCriticalFrames
  ) {
    return 'critical';
  }
  if (
    measured(playoutLatencyTrendMs) &&
    playoutLatencyTrendMs >= limits.playoutTrendCriticalMs
  ) {
    return 'critical';
  }
  if (decodeFpsApplies && measured(inputs.decodeFps) && inputs.decodeFps <= fpsLimits.critical) {
    return 'critical';
  }

  const queueMs = inputs.standingQueueMs;
  if (queueMs !== null && Number.isFinite(queueMs) && queueMs >= limits.queueEnterMs) {
    return 'critical';
  }

  // Elevated tier.
  if (decodeFpsApplies && measured(inputs.decodeFps) && inputs.decodeFps <= fpsLimits.elevated) {
    return 'elevated';
  }
  if (measured(inputs.packetsLostPct) && inputs.packetsLostPct >= limits.lossElevatedPct) {
    return 'elevated';
  }
  if (
    measured(playoutLatencyTrendMs) &&
    playoutLatencyTrendMs >= limits.playoutTrendElevatedMs
  ) {
    return 'elevated';
  }
  if (queueMs !== null && Number.isFinite(queueMs) && queueMs >= limits.sustainedQueueEnterMs) {
    return 'elevated';
  }

  // Nothing fired. Only call the link healthy if the queue measurement was trustworthy.
  if (queueMs === null || !Number.isFinite(queueMs)) {
    return 'insufficient-data';
  }
  return 'none';
}
